import { DateTime } from "luxon";
import {
  isoStringDateRangeToTimestampRange,
  timestampRangeToISOStringDateRange
} from "../helpers/utilities.js";
const defaultDurationInMinutes = 30;
const DAY_NAMES_ORDERED = [
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
  "sunday"
];
function isDayName(key) {
  return DAY_NAMES_ORDERED.includes(key);
}
function isValidTimeSlot(timeSlot) {
  if (!timeSlot || typeof timeSlot !== "object") return false;
  const slot = timeSlot;
  return typeof slot.start === "string" && typeof slot.end === "string" && /^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/.test(slot.start) && /^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/.test(slot.end);
}
function isValidWeeklyHours(weeklyHours) {
  if (!weeklyHours || typeof weeklyHours !== "object") return false;
  const hours = weeklyHours;
  return DAY_NAMES_ORDERED.every((day) => {
    const dayHours = hours[day];
    if (!Array.isArray(dayHours)) return false;
    return dayHours.every(isValidTimeSlot);
  });
}
function isValidSchedulingConfigurationSettings(settings) {
  if (!settings || typeof settings !== "object") return false;
  const { timezone, weekly_hours } = settings;
  if (!timezone || typeof timezone !== "string") return false;
  try {
    Intl.DateTimeFormat(void 0, { timeZone: timezone });
  } catch (e) {
    return false;
  }
  return isValidWeeklyHours(weekly_hours);
}
function timeToMinutes(time) {
  const [hours, minutes] = time.split(":").map(Number);
  if (hours === void 0 || minutes === void 0) {
    throw new Error(`Invalid time format: ${time}`);
  }
  return hours * 60 + minutes;
}
function minutesToTime(minutes) {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return `${hours.toString().padStart(2, "0")}:${mins.toString().padStart(2, "0")}`;
}
function consolidateTimeSlots(weeklyHours) {
  const consolidated = {};
  for (const day of DAY_NAMES_ORDERED) {
    consolidated[day] = [];
  }
  Object.entries(weeklyHours).forEach(([dayStr, slots]) => {
    const day = dayStr;
    if (!isDayName(day)) return;
    if (slots.length === 0) {
      consolidated[day] = [];
      return;
    }
    const timeRanges = slots.map((slot) => ({
      start: timeToMinutes(slot.start),
      end: timeToMinutes(slot.end)
    })).sort((a, b) => a.start - b.start);
    const mergedRanges = [];
    let currentRange = timeRanges[0];
    if (currentRange === void 0) {
      return;
    }
    for (let i = 1; i < timeRanges.length; i++) {
      const nextRange = timeRanges[i];
      if (nextRange === void 0) {
        continue;
      }
      if (currentRange.end >= nextRange.start - 1) {
        currentRange.end = Math.max(currentRange.end, nextRange.end);
      } else {
        mergedRanges.push(currentRange);
        currentRange = nextRange;
      }
    }
    mergedRanges.push(currentRange);
    consolidated[day] = mergedRanges.map((range) => ({
      start: minutesToTime(range.start),
      end: minutesToTime(range.end)
    }));
  });
  return consolidated;
}
function formatTimeForDisplay(time, use24HourFormat = false, includesWholeHourMinutes = false) {
  if (use24HourFormat) {
    return time;
  }
  const [hours, minutes] = time.split(":");
  if (hours === void 0) {
    throw new Error(`Invalid time format: ${time}`);
  }
  const hour = parseInt(hours, 10);
  const ampm = hour >= 12 ? "pm" : "am";
  const displayHour = hour % 12 || 12;
  if (minutes === "00" && !includesWholeHourMinutes) {
    return `${displayHour}${ampm}`;
  }
  return `${displayHour}:${minutes}${ampm}`;
}
function getAvailabilitySummary(weeklyHours) {
  if (!weeklyHours) {
    return "No availability";
  }
  const dayLabels = DAY_NAMES_ORDERED.map(
    (d) => d.charAt(0).toUpperCase() + d.slice(1, 3)
  );
  const dailyAvailabilityStrings = DAY_NAMES_ORDERED.map((dayId) => {
    const timeRanges = weeklyHours[dayId];
    if (!timeRanges || timeRanges.length === 0) {
      return "None";
    }
    return timeRanges.map(
      (tr) => `${formatTimeForDisplay(tr.start)}\u2013${formatTimeForDisplay(tr.end)}`
    ).join(", ");
  });
  const summaryBlocks = [];
  let startDayIndex = 0;
  for (let i = 1; i < DAY_NAMES_ORDERED.length; i++) {
    if (dailyAvailabilityStrings[i] !== dailyAvailabilityStrings[startDayIndex]) {
      const endDayIndex = i - 1;
      const timeString = dailyAvailabilityStrings[startDayIndex];
      const startLabel = dayLabels[startDayIndex];
      const endLabel = dayLabels[endDayIndex];
      const dayRange = startDayIndex === endDayIndex ? startLabel : `${startLabel}-${endLabel}`;
      summaryBlocks.push(`${dayRange}: ${timeString}`);
      startDayIndex = i;
    }
  }
  const finalEndDayIndex = DAY_NAMES_ORDERED.length - 1;
  const finalTimeString = dailyAvailabilityStrings[startDayIndex];
  const finalStartLabel = dayLabels[startDayIndex];
  const finalEndLabel = dayLabels[finalEndDayIndex];
  const finalDayRange = startDayIndex === finalEndDayIndex ? finalStartLabel : `${finalStartLabel}-${finalEndLabel}`;
  summaryBlocks.push(`${finalDayRange}: ${finalTimeString}`);
  return summaryBlocks.join(", ");
}
function getAvailabilityDateRanges(startDate, nextNbDays, schedulingConfigurationSettings, nonAvailableDateRanges) {
  const startDateTz = DateTime.fromJSDate(startDate, {
    zone: schedulingConfigurationSettings.timezone
  });
  const availableTimestampRangesFromSchedulingConfiguration = [];
  for (let i = 0; i <= nextNbDays; i++) {
    const currentDateTime = startDateTz.plus({ days: i });
    const timeSlots = getTimeSlotsForDay(
      currentDateTime.toJSDate(),
      schedulingConfigurationSettings
    );
    for (const timeSlot of timeSlots) {
      const [startHour, startMinute] = timeSlot.start.split(":").map(Number);
      const [endHour, endMinute] = timeSlot.end.split(":").map(Number);
      const start = currentDateTime.set({
        hour: startHour,
        minute: startMinute,
        second: 0,
        millisecond: 0
      }).toJSDate();
      const end = currentDateTime.set({
        hour: endHour,
        minute: endMinute,
        second: 0,
        millisecond: 0
      }).toJSDate();
      availableTimestampRangesFromSchedulingConfiguration.push({
        startTimestamp: start.getTime(),
        endTimestamp: end.getTime()
      });
    }
  }
  const nonAvailableTimestampRanges = nonAvailableDateRanges.map(
    isoStringDateRangeToTimestampRange
  );
  const availableTimestampRanges = subtractNonAvailableTimestampRanges(
    availableTimestampRangesFromSchedulingConfiguration,
    nonAvailableTimestampRanges
  );
  const meetingDurationInMillis = schedulingConfigurationSettings.duration * 60 * 1e3;
  const availableTimestampRangesForMeeting = availableTimestampRanges.filter(
    (timestampRange) => {
      const duration = timestampRange.endTimestamp - timestampRange.startTimestamp;
      return duration >= meetingDurationInMillis;
    }
  );
  return availableTimestampRangesForMeeting.map(
    timestampRangeToISOStringDateRange
  );
}
function getTimeSlotsForDay(date, schedulingConfigurationSettings) {
  const dateTz = DateTime.fromJSDate(date, {
    zone: schedulingConfigurationSettings.timezone
  });
  const day = dateTz.weekdayLong?.toLowerCase();
  if (!day) {
    return [];
  }
  return schedulingConfigurationSettings.weekly_hours[day];
}
function subtractNonAvailableTimestampRanges(availableRanges, nonAvailableRanges) {
  if (nonAvailableRanges.length === 0) {
    return availableRanges;
  }
  availableRanges.sort((a, b) => a.startTimestamp - b.startTimestamp);
  nonAvailableRanges.sort((a, b) => a.startTimestamp - b.startTimestamp);
  const result = [];
  for (const availRange of availableRanges) {
    let remainingParts = [availRange];
    for (const nonAvailRange of nonAvailableRanges) {
      const nextParts = [];
      for (const part of remainingParts) {
        if (nonAvailRange.endTimestamp <= part.startTimestamp || nonAvailRange.startTimestamp >= part.endTimestamp) {
          nextParts.push(part);
        } else {
          if (nonAvailRange.startTimestamp > part.startTimestamp) {
            nextParts.push({
              startTimestamp: part.startTimestamp,
              endTimestamp: nonAvailRange.startTimestamp
            });
          }
          if (nonAvailRange.endTimestamp < part.endTimestamp) {
            nextParts.push({
              startTimestamp: nonAvailRange.endTimestamp,
              endTimestamp: part.endTimestamp
            });
          }
        }
      }
      remainingParts = nextParts;
      if (remainingParts.length === 0) {
        break;
      }
    }
    result.push(...remainingParts);
  }
  return mergeAdjacentRanges(result);
}
function mergeAdjacentRanges(ranges) {
  if (ranges.length === 0) {
    return [];
  }
  const sortedRanges = ranges.toSorted(
    (a, b) => a.startTimestamp - b.startTimestamp
  );
  const [firstRange] = sortedRanges;
  if (firstRange === void 0) {
    return [];
  }
  const merged = [{ ...firstRange }];
  for (let i = 1; i < sortedRanges.length; i++) {
    const last = merged[merged.length - 1];
    const current = sortedRanges[i];
    if (last === void 0 || current === void 0) {
      continue;
    }
    if (current.startTimestamp <= last.endTimestamp) {
      last.endTimestamp = Math.max(last.endTimestamp, current.endTimestamp);
    } else {
      merged.push(current);
    }
  }
  return merged;
}
export {
  DAY_NAMES_ORDERED,
  consolidateTimeSlots,
  defaultDurationInMinutes,
  formatTimeForDisplay,
  getAvailabilityDateRanges,
  getAvailabilitySummary,
  isValidSchedulingConfigurationSettings,
  subtractNonAvailableTimestampRanges
};
//# sourceMappingURL=schedulingConfiguration.js.map
